import { useState, useEffect, useRef } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import useApi from '../../utils/useApi';
import { formatEpochToLocalDate } from '../../utils/dateTimeConvertor';
import Toaster from '../Toaster/Toaster.jsx';
import ErrorPage from '../Error/Error.jsx';
import '../../css/common-styles.css';
import '../../css/common-forms.css';
import noDocumentsImage from '../../images/no_document.svg';
import sortedIcon from '../../images/sorted_icon.svg';
import warningIcon from '../../images/warning-icon.svg';
import loadIcon from '../../images/loader-transparent.svg';

import styles from './ViewDocuments.module.css';

const ViewDocuments = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const initialObjectType = urlParams.get('objectType') || '';
  const initialRecordId = urlParams.get('recordId') || '';
  const initialPopup = urlParams.get('popup') || '';

  // Use the extracted values to initialize state
  const [objectType] = useState(initialObjectType);
  const [recordId] = useState(initialRecordId);
  const [popup] = useState(initialPopup);
  
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const activeTab = 'all';
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [error, setError] = useState(null);
  
  // Revocation modal states
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revocationReason, setRevocationReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  const [revocationError, setRevocationError] = useState('');
  
  // Reminder modal states
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [toasterMessage, setToasterMessage] = useState({ show: false, message: '', type: 'error' });
  const [showErrorPage, setShowErrorPage] = useState(false);
  
  // Signer selection states for reminder
  const [isLoadingSigners, setIsLoadingSigners] = useState(false);
  const [signers, setSigners] = useState([]); // {name,email,status,canSelect}
  const [selectedSignerEmails, setSelectedSignerEmails] = useState([]);
  const [selectedSignerPhoneNumbers, setSelectedSignerPhoneNumbers] = useState([]);
  const selectAllCheckboxRef = useRef(null);

  const [errorPageConfig, setErrorPageConfig] = useState({
    title: "Unable to Load Documents",
    message: "We couldn't retrieve your document details. Please close this and try again later.",
    buttonText: "Close"
  });
  const [fetchRetryCount, setFetchRetryCount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Sort state for "Sent on" column (default: newest first)
  const [sentOnSortOrder, setSentOnSortOrder] = useState('desc'); // 'asc' | 'desc'

  // Status options with different colors for badges
  const statusOptions = {
    'waiting_for_me': { label: 'Waiting for me'},
    'waiting_for_others': { label: 'Waiting for others'},
    'needs_attention': { label: 'Needs Attention'},
    'completed': { label: 'Completed' },
    'declined': { label: 'Declined' },
    'expired': { label: 'Expired' },
    'revoked': { label: 'Revoked' }
  };

  const getDeliveryOptionFromNumber = (deliveryModeNumber) => {
    const DeliveryModeMapping = {
      0: 'Email',
      1: 'SMS',
      2: 'EmailAndSMS',
    };

    return DeliveryModeMapping[deliveryModeNumber] || 'Email';
  };

  const deliveryModeToShow  = (signers) => {
    if(signers.deliveryMode === 'Email'){
      return signers.email;
    }
    else if(signers.deliveryMode === 'SMS'){
      return `${signers.phoneNumber?.countryCode} ${signers.phoneNumber?.number}`;
    }
    return `${signers.email} / ${signers.phoneNumber?.countryCode} ${signers.phoneNumber?.number}`;
  }
  
  const isSignerAllSelected = (s) => {
    const emailSelected =
      s.deliveryMode === 'Email' || s.deliveryMode === 'EmailAndSMS'
        ? selectedSignerEmails.includes(s.email)
        : true;

    const phoneSelected =
      s.deliveryMode === 'SMS' || s.deliveryMode === 'EmailAndSMS'
        ? selectedSignerPhoneNumbers.includes(
            s.phoneNumber?.countryCode + s.phoneNumber?.number
          )
        : true;

    return emailSelected && phoneSelected;
  };

  const isSingleSignerSelected = (signer) => {
    const phone = signer.phoneNumber?.countryCode + signer.phoneNumber?.number;

    if (signer.deliveryMode === 'Email') {
      return selectedSignerEmails.includes(signer.email);
    }

    if (signer.deliveryMode === 'SMS') {
      return selectedSignerPhoneNumbers.includes(phone);
    }

    if (signer.deliveryMode === 'EmailAndSMS') {
      return (
        selectedSignerEmails.includes(signer.email) &&
        selectedSignerPhoneNumbers.includes(phone)
      );
    }

    return false;
  };
  const api = useApi();

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, recordId]); // Add all relevant dependencies

  useEffect(() => {
    let connection;
    let isDisposed = false;

    const connectToDocumentStatusService = async () => {
      connection = new HubConnectionBuilder()
        .withUrl('/status/update-document-status')
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('DocumentStatusUpdated', event => {
        if (!event?.documentId || !event?.status) {
          return;
        }

        setDocuments(prev =>
          prev.map(document =>
            document.id === event.documentId
              ? {
                  ...document,
                  status: mapStatusToUiStatus(event.status),
                }
              : document
          )
        );
      });

      connection.onreconnected(async () => {
        if (!isDisposed) {
          // Refresh data silently after a reconnect so the existing table does not
          // disappear while the SignalR connection is recovering.
          await fetchDocuments(false);
        }
      });

      try {
        await connection.start();

        if (!isDisposed) {
          await connection.invoke('RegisterPortalID');
        }
      } catch (updateError) {
        console.error('Unable to update to document status:', updateError);
      }
    };

    connectToDocumentStatusService();

    return () => {
      isDisposed = true;
      if (connection) {
        connection.off('DocumentStatusUpdated');
        connection.stop();
      }
    };
    // The initial fetch and hub lifecycle are scoped to the current record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, recordId]);

  // Toaster functions
  const showToaster = (message, type = 'error') => {
    setToasterMessage({ show: true, message, type });
  };

  const hideToaster = () => {
    setToasterMessage({ show: false, message: '', type: 'error' });
  };
  
  // Daily reminder limit helpers (client-side validation)
  const getReminderStorageKey = (docId) => `bs_manual_reminder_last_sent:${docId}`;
  const hasReachedDailyReminderLimit = (docId) => {
    try {
      const lastSentIso = localStorage.getItem(getReminderStorageKey(docId));
      if (!lastSentIso) return false;
      const last = new Date(lastSentIso);
      const now = new Date();
      // Enforce one reminder per calendar day
      return last.toDateString() === now.toDateString();
    } catch (e) {
      return false;
    }
  };
  const recordReminderSent = (docId) => {
    try {
      localStorage.setItem(getReminderStorageKey(docId), new Date().toISOString());
    } catch (e) {
      // ignore storage errors
    }
  };

  // Map backend status to frontend status format
  const mapStatusToUiStatus = (status) => {
    // Map backend status values to UI status values
    // This mapping can be adjusted based on actual backend values
    const statusMap = {
      "Completed": "completed",
      "WaitingForOthers": "waiting_for_others",
      "WaitingForMe": "waiting_for_me",
      "NeedAttention": "needs_attention",
      "Expired": "expired",
      "Revoked": "revoked",
      "Declined": "declined"
    };
    
    return statusMap[status] || "waiting_for_others"; // Default fallback
  };





  const fetchDocuments = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      // If still missing required parameters, show error
      if (!objectType || !recordId) {
        setError('Missing required information. Please check the URL and try again.');
        if (showLoading) {
          setIsLoading(false);
        }
        return;
      }
      
      // Fetch documents from the backend API
      const response = await api.get(`/boldsign/view-documents`,
        {
          params:{
            recordId,
            objectType
          }
        }
      );
      
      if (response.status !== 200) {
        throw new Error(`Database fetch failed with status: ${response.status}`);
      }
      
      const data = await response.data;
      // Transform the API response to match the UI format
      const formattedDocuments = data.map(doc => {
        const raw = Number(doc.sentOn);
        const sentOnMs = Number.isFinite(raw)
          ? (raw > 0 && raw < 1e12 ? raw * 1000 : raw)
          : 0;
        return {
          id: doc.id,
          title: doc.title || 'Untitled Document',
          status: mapStatusToUiStatus(doc.status),
          sentOnEpochMs: sentOnMs,
          senderName: doc.senderName || 'Not Available',
          baseUrl: doc.baseUrl,
          hasExternalLink: true,
          isOwner: doc.isOwner || false,
          // Try to resolve recipients list from multiple possible backend fields
          recipients: Array.isArray(doc.recipients)
            ? doc.recipients
            : Array.isArray(doc.recipientNames)
              ? doc.recipientNames
              : Array.isArray(doc.signers)
                ? doc.signers.map(s => s.name).filter(Boolean)
                : []
        };
      });
      
      setDocuments(formattedDocuments);
      
      // Reset retry count on successful fetch
      setFetchRetryCount(0);
      
      // For empty state, clear any previous error and allow UI to render the empty view
      if (formattedDocuments.length === 0) {
        setError(null);
      } else {
        setError(null);
      }
      
      if (showLoading) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      
      // Increment retry count
      const newRetryCount = fetchRetryCount + 1;
      setFetchRetryCount(newRetryCount);
      
      // Check if we've reached maximum retries
      if (newRetryCount >= 3) {
        // Show error page after 3 failed attempts
        setErrorPageConfig({
          title: "Unable to Load Documents",
          message: "We couldn't retrieve your document details. Please close this and try again later.",
          buttonText: "Close"
        });
        setShowErrorPage(true);
        if (showLoading) {
          setIsLoading(false);
        }
        return;
      }
      
      // Show user-friendly toaster message for retry attempts
      showToaster('Unable to retrieve documents. Please try again.', 'error');
      setError('Unable to load documents at this time.');
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  // Get filtered documents based on active tab
  const filteredDocuments = activeTab === 'all' 
    ? documents 
    : documents.filter(doc => doc.status === activeTab);

  // Sort by Sent on (newest first) using epoch milliseconds
  const sortedDocuments = (filteredDocuments || [])
    .slice()
    .sort((a, b) => {
      const aTime = a?.sentOnEpochMs || 0;
      const bTime = b?.sentOnEpochMs || 0;
      return sentOnSortOrder === 'asc' ? aTime - bTime : bTime - aTime;
    });

  // Pagination derived values
  const total = sortedDocuments ? sortedDocuments.length : 0;
  const listStartIndex = (currentPage - 1) * pageSize;
  const listEndIndex = Math.min(listStartIndex + pageSize, total);
  const pagedDocuments = sortedDocuments ? sortedDocuments.slice(listStartIndex, listEndIndex) : [];

  // Reset to first page when data set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortedDocuments.length]);

  // Handle document actions
  const handleActionClick = (doc, e) => {
    e.stopPropagation();
    setSelectedDocument(doc);
    setShowActionMenu(true);
  };

  const handleActionOption = (action) => {
    if (!selectedDocument) return;
    
    switch(action) {
      case 'signNow':
        // Open sign page in new tab
        if (selectedDocument.baseUrl) {
          window.open(`${selectedDocument.baseUrl}/document/sign/?documentId=${selectedDocument.id}`, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'overview':
        // Open overview in new tab
        if (selectedDocument.baseUrl) {
          window.open(`${selectedDocument.baseUrl}/documents/mydocuments/overview/?documentId=${selectedDocument.id}`, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'view':
        // Open in new tab
        if (selectedDocument.baseUrl) {
          window.open(`${selectedDocument.baseUrl}/document/view/?documentId=${selectedDocument.id}`, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'remind':
        // Fetch signers in background when action menu opens
        fetchSignersForDocument(selectedDocument.id);
        // Show reminder modal; also fetch signers for selection
        setShowReminderModal(true);
        break;
      case 'revoke':
        // Show revoke modal instead of immediately revoking
        setShowRevokeModal(true);
        break;
      default:
        break;
    }
    
    setShowActionMenu(false);
  };

  // Handle the document revocation process
  const handleRevokeDocument = async () => {
    if (!selectedDocument) return;
    
    setIsRevoking(true);
    setRevocationError('');

    // Store the reason before potential modal close
    const reasonToUse = revocationReason;

    // Close modal after a brief moment to show the action was registered
    setTimeout(() => {
      setShowRevokeModal(false);
      setRevocationReason('');
      setRevocationError('');
    }, 500);

    try {

      const revokeRequest = {
        reason: reasonToUse
      }
      
      // Call the API to revoke the document
      const response = await api.post(
        `/boldsign/revoke-document`,
        revokeRequest,
        {
          params:{
            documentId:selectedDocument.id
          },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // Update document status in the UI
        const updatedDocuments = documents.map(doc => 
          doc.id === selectedDocument.id 
            ? { ...doc, status: 'revoked' } 
            : doc
        );
        
        setDocuments(updatedDocuments);

        // Show success toaster for revoke action
        showToaster('Document revoked successfully!', 'success');
      }
      else if (response.status !== 204) {
        throw new Error(`Failed to revoke document`);
      }
    } catch (error) {
      console.error('Error revoking document:', error);
      
      // Extract meaningful error message from the response or error object
      let errorMessage = 'Unable to revoke document. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error in toaster
      showToaster(errorMessage, 'error');
    } finally {
      setIsRevoking(false);
    }
  };

  // Close the revoke modal
  const handleCloseRevokeModal = () => {
    setShowRevokeModal(false);
    setRevocationReason('');
    setRevocationError('');
  };

  // Fetch signer list for a document
  const fetchSignersForDocument = async (documentId) => {
    // Show loading state but keep modal responsive
    setIsLoadingSigners(true);
    try {
      const response = await api.get(`/boldsign/document-signers`, {
        params: { documentId }
      });
      if (response.status !== 200) {
        throw new Error(`Failed to load signers: ${response.status}`);
      }
      const data = await response.data;
      data.forEach(s => {
        s.deliveryMode = getDeliveryOptionFromNumber(s.deliveryMode);
      });
      setSigners(Array.isArray(data) ? data : []);

      // Pre-check all selectable signers (by email and phone based on delivery mode)
      const selectableSigners = (Array.isArray(data) ? data : []).filter(s => s.canSelect);
      const emailsToSelect = [];
      const phonesToSelect = [];
      
      selectableSigners.forEach(signer => {
        // Add email if delivery mode supports it (Email or EmailAndSMS)
        if (signer.deliveryMode === 'Email' || signer.deliveryMode === 'EmailAndSMS') {
          emailsToSelect.push(signer.email);
        }
        // Add phone if delivery mode supports it (SMS or EmailAndSMS)
        if ((signer.deliveryMode === 'SMS' || signer.deliveryMode === 'EmailAndSMS') && signer.phoneNumber?.number) {
          phonesToSelect.push(signer.phoneNumber.countryCode + signer.phoneNumber.number);
        }
      });
      
      setSelectedSignerEmails(emailsToSelect);
      setSelectedSignerPhoneNumbers(phonesToSelect);
    } catch (err) {
      console.error('Error loading signers', err);
      showToaster('Unable to load signers for this document.', 'error');
    } finally {
      setIsLoadingSigners(false);
    }
  };

  // Toggle signer selection (now takes signer object)
  const toggleSignerSelection = (signer) => {
  if (!signer.canSelect) return;

  // Email toggle
  if (signer.deliveryMode === 'Email' || signer.deliveryMode === 'EmailAndSMS') {
    setSelectedSignerEmails(prev =>
      prev.includes(signer.email) ? prev.filter(e => e !== signer.email) : [...prev, signer.email]
    );
  }

  // Phone toggle
  if (signer.deliveryMode === 'SMS' || signer.deliveryMode === 'EmailAndSMS') {
    const phoneValue = signer.phoneNumber.countryCode + signer.phoneNumber.number;

    setSelectedSignerPhoneNumbers(prev =>
      prev.includes(phoneValue) ? prev.filter(p => p !== phoneValue) : [...prev, phoneValue]
    );
  }
};

  // Toggle Select All for signers
  const toggleSelectAllSigners = () => {
    const selectableSigners = signers.filter(s => s.canSelect);
    // Count how many signers are fully selected (email + phone if applicable)
    const fullySelectedCount = selectableSigners.filter(s => {
      const emailSelected = (s.deliveryMode === 'Email' || s.deliveryMode === 'EmailAndSMS') ? selectedSignerEmails.includes(s.email) : true;
      const phoneSelected = (s.deliveryMode === 'SMS' || s.deliveryMode === 'EmailAndSMS') ? selectedSignerPhoneNumbers.includes(s.phoneNumber.countryCode + s.phoneNumber.number) : true;

      return emailSelected && phoneSelected;
    }).length;
      
    if (fullySelectedCount === selectableSigners.length) {
      // If all are selected, deselect all
      setSelectedSignerEmails([]);
      setSelectedSignerPhoneNumbers([]);
    } else {
      // Select all selectable signers
      const emailsToSelect = [];
      const phonesToSelect = [];
      
      selectableSigners.forEach(s => {
        if (s.deliveryMode === 'Email' || s.deliveryMode === 'EmailAndSMS') {
          emailsToSelect.push(s.email);
        }
        if (s.deliveryMode === 'SMS' || s.deliveryMode === 'EmailAndSMS') {
          phonesToSelect.push(s.phoneNumber.countryCode + s.phoneNumber.number);
        }
      });
      
      setSelectedSignerEmails(emailsToSelect);
      setSelectedSignerPhoneNumbers(phonesToSelect);
    }
  };

  // Update Select All checkbox indeterminate state
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const selectableSigners = signers.filter(s => s.canSelect);
      // Count fully selected signers
      const fullySelectedCount = selectableSigners.filter(s => {
        const emailSelected = (s.deliveryMode === 'Email' || s.deliveryMode === 'EmailAndSMS') ? selectedSignerEmails.includes(s.email) : true;
        const phoneSelected = (s.deliveryMode === 'SMS' || s.deliveryMode === 'EmailAndSMS') ? selectedSignerPhoneNumbers.includes(s.phoneNumber.countryCode + s.phoneNumber.number) : true;
        
        return emailSelected && phoneSelected;
      }).length;
      
      const selectableCount = selectableSigners.length;
      
      // Set indeterminate state when some (but not all) are selected
      selectAllCheckboxRef.current.indeterminate = fullySelectedCount > 0 && fullySelectedCount < selectableCount;
    }
  }, [selectedSignerEmails, selectedSignerPhoneNumbers, signers]);
  const selectedSignerCount = signers.filter(s => {
    if (!s.canSelect) return false;

    const emailSelected = s.deliveryMode === 'Email' || s.deliveryMode === 'EmailAndSMS' ? selectedSignerEmails.includes(s.email) : true;

    const phoneSelected = s.deliveryMode === 'SMS' || s.deliveryMode === 'EmailAndSMS' ? selectedSignerPhoneNumbers.includes(s.phoneNumber?.countryCode + s.phoneNumber?.number) : true;

    return emailSelected && phoneSelected;
  }).length;

  // Handle sending reminders via email and/or SMS
  const handleSendReminder = async () => {
    if (!selectedDocument) return;

    // Validate that at least one recipient is selected
    if (selectedSignerEmails.length === 0 && selectedSignerPhoneNumbers.length === 0) {
      setReminderError('Please select at least one recipient (email or SMS).');
      return;
    }

    // Client-side validation: one manual reminder per day (validate on submit)
    if (hasReachedDailyReminderLimit(selectedDocument.id)) {
      // Close modal and show error notification
      setShowReminderModal(false);
      setReminderMessage('');
      setReminderError('');
      showToaster('Your manual reminders limit has been reached so you cannot send another reminder today', 'error');
      return;
    }
        
    setIsSendingReminder(true);
    setReminderError('');

      // Store the message before potential modal close
      const messageToUse = reminderMessage || 'Please sign this document at your earliest convenience.';
      // Close modal after a brief moment to show the action was registered
      setTimeout(() => {
        setShowReminderModal(false);
        setReminderMessage('');
        setReminderError('');
      }, 500);

    try {
      const formattedPhoneNumbers = signers
        .filter(phone => phone.deliveryMode === 'SMS' ||  phone.deliveryMode === 'EmailAndSMS')
        .map(phone => ({ countryCode: phone.phoneNumber.countryCode, number: phone.phoneNumber.number }));

      const remindRequest = {
        message: messageToUse,
        receiverEmails: selectedSignerEmails,
        receiverPhoneNumbers: formattedPhoneNumbers,
      };

      // Call the API to send reminders
      const response = await api.post(
        `/boldsign/send-reminder`,
        remindRequest,
        {
          params: {
            documentId: selectedDocument.id
          },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status !== 200) {
        throw new Error(`Failed to send reminder`);
      }
      
      // Record reminder as sent for daily limit enforcement
      recordReminderSent(selectedDocument.id);

      // Show success toaster
      showToaster('Reminder sent successfully!', 'success');

    } catch (error) {
      console.error('Error sending reminder:', error);
      
      // Extract meaningful error message from the response or error object
      let errorMessage = 'Unable to send reminder. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error in toaster
      showToaster(errorMessage, 'error');
    } finally {
      setIsSendingReminder(false);
    }
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showActionMenu) {
        // Use data attributes to avoid CSS module selector issues
        const actionButton = e.target.closest('[data-action-button]');
        const actionMenu = e.target.closest('[data-action-menu]');
        const actionCell = e.target.closest('[data-action-cell]');

        if (!actionButton && !actionMenu && !actionCell) {
          setShowActionMenu(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionMenu]);

  // Get status indicator based on status
  const getStatusIndicator = (status) => {
    switch(status) {
      case 'completed':
        return <div className={styles.sfIconCompleted}></div>;
      case 'waiting_for_others':
        return <div className={styles.sfIconTime}></div>;
      case 'waiting_for_me':
        return <div className={styles.sfIconPen}></div>;
      case 'needs_attention':
        return <div className={styles.sfIconInformation}></div>;
      case 'expired':
        return <div className={styles.sfIconCircleClose}></div>;
      case 'revoked':
        return <div className={styles.sfIconRemove}></div>;
      case 'declined':
        return <div className={styles.sfIconDeclined}></div>;
      default:
        return null;
    }
  };

  // Toggle sort order for "Sent on"
  const toggleSentOnSort = () => {
    setSentOnSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    // Reset to first page when sort changes to keep UX predictable
    setCurrentPage(1);
  };

  // Handle error page button click
  const handleErrorPageButtonClick = () => {
    // Send DONE message to parent to close the modal/iframe
    try {
      window.parent.postMessage(JSON.stringify({ action: "DONE" }), "*");
    } catch (error) {
      console.error("Error occurred when return to HubSpot:", error);
      // Fallback: try to go back in history
      if (window.history.length > 1) {
        window.history.back();
      }
    }
  };

  // Show error page if needed
  if (showErrorPage) {
    return (
      <ErrorPage
        title={errorPageConfig.title}
        message={errorPageConfig.message}
        buttonText={errorPageConfig.buttonText}
        onButtonClick={handleErrorPageButtonClick}
      />
    );
  }

  const handleCreateNewDocument = () => {
    const origin = window.location.origin;
    const url = new URL(`${origin}/send`);
    url.searchParams.set('recordId', recordId);
    url.searchParams.set('objectType', objectType);
    if (popup) {
      url.searchParams.set('popup', '1');
    }
    window.location.href = url.toString();
  };

  return (
    <div className={styles.documentsWrapper}>
      {/* Toaster Component */}
      <Toaster
        show={toasterMessage.show}
        message={toasterMessage.message}
        type={toasterMessage.type}
        onClose={hideToaster}
        autoHide={true}
        autoHideDelay={3000}
      />
      
      {/* Documents table */}
      <div className={styles.tableSection}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <img src={loadIcon} alt="loading" className="spinner-circle"/>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
              <div className={styles.errorIconAndMessage}>
                <div className={styles.errorIcon}>
                  <span className={styles.sfIconInformation}></span>
                </div>
                <div className={styles.errorMessage}>
                  {error}
                </div>
              </div>
            {error === 'Unable to load documents at this time.' && fetchRetryCount < 3 && (
              <div className={styles.tryAgainContainer}>
                <button 
                  className={styles.tryAgainButton}
                  onClick={fetchDocuments}
                  disabled={isLoading}
                >
                  {isLoading ? 'Trying...' : 'Try Again'}
                </button>
              </div>
            )}
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className={styles.documentsTableContainer}>
            <div>
            <table className={styles.documentsTable}>
              <thead>
                <tr>
                  <th className={styles.titleColumn}>Title</th>
                  <th className={styles.recipientsColumn}>Recipients</th>
                  <th className={styles.sentColumn}>
                    <div className={styles.sortHeader}>
                      <span>Sent on</span>
                      <button
                        type="button"
                        className={styles.sortIconButton}
                        onClick={toggleSentOnSort}
                        aria-label={`Sort by sent date ${sentOnSortOrder === 'asc' ? 'descending' : 'ascending'}`}
                      >
                        <img
                          src={sortedIcon}
                          alt=""
                          className={`${styles.sortIcon} ${sentOnSortOrder === 'asc' ? styles.asc : styles.desc}`}
                        />
                      </button>
                    </div>
                  </th>
                  <th className={styles.statusColumn}>Status</th>
                  <th className={styles.actionColumn}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedDocuments.map((doc) => {
                  const recipients = Array.isArray(doc.recipients) ? doc.recipients.filter(Boolean) : [];
                  const firstTwo = recipients.slice(0, 2);
                  const remaining = recipients.slice(2);
                  const tooltipId = `recipients-tt-${doc.id}`;
                  return (
                    <tr key={doc.id} className={styles.documentRow}>
                      <td className={styles.titleColumn}>
                        <div
                          className={`${styles.cellContent} ${styles.titleCellContent}`}
                        >
                          <div className={styles.titleWrapper}>
                              {doc.isOwner ? (
                                <span
                                  className={`${styles.documentTitleLink} ${styles.ownerTitleLink}`}
                                  aria-label={doc.title}
                                >
                                  <span className={styles.documentTitleRow}>
                                    <span className={styles.titleText}>{doc.title}</span>
                                  </span>
                                </span>
                              ) : (
                                <span
                                  className={`${styles.documentTitleLink} ${styles.nonOwnerTitleLink} ${styles.disabledTitleLink}`}
                                  aria-label={doc.title}
                                >
                                  <span className={styles.documentTitleRow}>
                                    <span className={styles.titleText}>{doc.title}</span>
                                  </span>
                                </span>
                              )}
                              <div className={styles.sentByUnderTitle}>Sent by: <span>{doc.isOwner ? 'You' : doc.senderName}</span></div>
                          </div>
                        </div>
                      </td>
                      <td className={styles.recipientsColumn}>
                        <div className={styles.cellContent}>
                          <div className={styles.recipientList}>
                            {firstTwo.map((name, idx) => (
                              <span key={idx} className={styles.recipientItem}>
                                {name}{(idx < firstTwo.length - 1 || remaining.length > 0) ? ', ' : ''}
                              </span>
                            ))}
                            {remaining.length > 0 && (
                              <span
                                className={styles.moreCount}
                                data-tooltip-id={tooltipId}
                                data-tooltip-content={remaining.join(', ')}
                              >
                                <span className={`${styles.sfAddIcon} sfIconBase sfAddIcon`}></span>
                                <span>{remaining.length} more</span>
                              </span>
                            )}
                            {remaining.length > 0 && (
                              <Tooltip id={tooltipId} place="top" className={`${styles.tooltipInfo} tooltip-info`} />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={styles.sentColumn}>
                        <div className={styles.cellContent}>
                          <span className={styles.sentDateCell}>{formatEpochToLocalDate(doc.sentOnEpochMs)}</span>
                        </div>
                      </td>
                      <td className={styles.statusColumn}>
                        <div className={styles.cellContent}>
                          <div className={styles.statusCell}>
                            {getStatusIndicator(doc.status)}
                            <span className={styles.statusText}>{statusOptions[doc.status].label}</span>
                          </div>
                        </div>
                      </td>
                      <td className={styles.actionColumn}>
                        <div className={styles.cellContent}>
                        <div className={styles.actionCell} data-action-cell>
                          {/* Wrap disabled button with span to allow tooltip since disabled buttons don't receive pointer events */}
                          {(!doc.isOwner) ? (
                            <span
                              data-tooltip-id={`action-tt-${doc.id}`}
                              data-tooltip-content="You cannot perform any action because you are not the sender of this document."
                              style={{ display: 'inline-flex' }}
                            >
                              <button 
                                className={styles.actionButton}
                                data-action-button
                                onClick={(e) => handleActionClick(doc, e)}
                                aria-label="Document actions"
                                disabled
                              >
                                <span className={styles.kebabIcon}></span>
                              </button>
                              <Tooltip id={`action-tt-${doc.id}`} place="top" className='tooltip-info' />
                            </span>
                          ) : (
                            <button 
                              className={styles.actionButton}
                              data-action-button
                              onClick={(e) => handleActionClick(doc, e)}
                              aria-label="Document actions"
                            >
                              <span className={styles.kebabIcon}></span>
                            </button>
                          )}
                          {showActionMenu && selectedDocument?.id === doc.id && (
                            <div className={styles.actionMenu} data-action-menu>
                              <button className={styles.actionOption} onClick={() => handleActionOption('overview')}>
                                <span className={styles.sfIconInfo}></span> Overview
                              </button>
                              {(doc.status === 'waiting_for_me') && (
                                <>
                                  <button className={styles.actionOption} onClick={() => handleActionOption('signNow')}>
                                    <span className={`${styles.sfSignNow}`}></span> Sign document
                                  </button>
                                </>
                              )}
                              <button className={styles.actionOption} onClick={() => handleActionOption('view')}>
                                <span className={styles.sfIconPreview}></span> View document
                              </button>
                              {(doc.status === 'waiting_for_others' || doc.status === 'waiting_for_me') && (
                                <>
                                  <button className={styles.actionOption} onClick={() => handleActionOption('remind')}>
                                    <span className={styles.sfIconSendReminder}></span> Send reminder
                                  </button>
                                  <button className={styles.actionOption} onClick={() => handleActionOption('revoke')}>
                                    <span className={styles.sfIconRevoke1}></span> Revoke
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={styles.paginationBar}>
              <span className={styles.paginationInfo}>{`${total === 0 ? 0 : listStartIndex + 1} - ${listEndIndex} of ${total}`}</span>
              <div className={styles.paginationActions}>
                <button
                  className={styles.pageNavBtn}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous"
                >
                  <span className="sfIconBase sfLeftArrowIcon"></span>
                </button>
                <button
                  className={styles.pageNavBtn}
                  onClick={() => setCurrentPage(p => (listEndIndex >= total ? p : p + 1))}
                  disabled={listEndIndex >= total}
                  aria-label="Next"
                >
                  <span className="sfIconBase sfRightArrowIcon"></span>
                </button>
              </div>
            </div>
            </div>
          </div>
        ) : (
          <div className={styles.emptyStateWrapper}>
            <div className={styles.emptyStateContent}>
              <img src={noDocumentsImage} alt="No documents" className={styles.emptyStateImage} />
              <h3 className={styles.emptyStateTitle}>No documents available!</h3>
              <p className={styles.emptyStateSubtitle}>
                You haven’t sent any documents for this record yet. Start by creating a new document and send it for signature directly from your CRM.
              </p>
              <button className={styles.createNewDocButton} onClick={handleCreateNewDocument}>
                <span className={`${styles.plusIcon} sfIconBase sfAddIcon`}></span>
                Create new document
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Revocation Modal */}
      {showRevokeModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.revokeModal}>
            <div className={styles.revokeModalHeader}>
              <div className={styles.revokeModalIcon}>
                <img
                  src={warningIcon}
                  alt=""
                  className={styles.warningIcon}
                />
              </div>
              <h3>Revoke</h3>
              <button 
                className={styles.closeButton} 
                onClick={handleCloseRevokeModal}
                aria-label="Close"
              >
                <span className={`${styles.closeIcon} sfIconBase sfCloseIcon`}></span>
              </button>
            </div>
            
            <div className={styles.revokeModalBody}>
              <p className={styles.revokeQuestion}>Are you sure you want to revoke this document?</p>
              
              {revocationError && (
                <div className={styles.revokeError}>
                  <div className={styles.revokeErrorIcon}>
                    <span className={styles.sfIconWarning}></span>
                  </div>
                  <p>{revocationError}</p>
                </div>
              )}
              
              <textarea
                className={styles.revokeReasonInput}
                placeholder="Enter reason for revocation*"
                value={revocationReason}
                onChange={(e) => setRevocationReason(e.target.value)}
                required
                rows={4}
              />
              
              <div className={styles.revokeNote}>
                <p><span className={styles.revokeNoteFontWeight}>Note:</span> By revoking this document, all signers will be notified with the given reason and they can no longer sign the document.</p>
              </div>
              
              <div className={styles.revokeActions}>
                <button 
                  className={styles.cancelButton} 
                  onClick={handleCloseRevokeModal} 
                  disabled={isRevoking}
                >
                  No
                </button>
                <button 
                  className={styles.revokeButton} 
                  onClick={handleRevokeDocument} 
                  disabled={isRevoking || !revocationReason.trim()}
                >
                  {isRevoking ? 'Revoking...' : 'Yes, revoke'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.remindModel}>
            <div className={styles.revokeModalHeader}>
              <h3>Remind Now</h3>
              <button 
                className={styles.closeButton} 
                onClick={() => {
                  setShowReminderModal(false);
                  setReminderMessage('');
                  setReminderError('');
                }}
                aria-label="Close"
              >
                <span className={`${styles.closeIcon} sfIconBase sfCloseIcon`}></span>
              </button>
            </div>
            
            <div className={styles.revokeModalBody}>
              {/* Signer selection */}
              <div className={styles.recipientsHeaderRow}>
                <div className={styles.recipientsHeader}>Recipients ({signers.length})</div>
                {selectedSignerEmails.length > 0 ? (
                  <div className={styles.selectedCount}>
                    {selectedSignerCount} selected
                  </div>
                ) : null}
              </div>
              <div className={`${styles.recipientsBox} ${styles.reminderModalRecipients}`}>
                <div className={styles.recipientList}>
                  {isLoadingSigners && signers.length === 0 ? (
                    <div className={styles.loadingRecipients}>
                      Loading recipients...
                    </div>
                  ) : (
                    <>
                      {signers.filter(s => s.canSelect).length > 1 && (
                        <label className={`${styles.recipientItem} ${styles.selectAllRow}`} htmlFor="select-all-signers">
                          <input
                            ref={selectAllCheckboxRef}
                            id="select-all-signers"
                            type="checkbox"
                            checked={signers.filter(s => s.canSelect).every(s => isSignerAllSelected(s))}
                            onChange={toggleSelectAllSigners}
                            aria-label="Select all signers"
                          />
                          <div className={styles.recipientInfo}>
                            <div className={styles.recipientName} style={{ fontWeight: '500' }}>Select All</div>
                          </div>
                        </label>
                      )}
                      {signers.map((s, idx) => (
                        <label key={idx} className={styles.recipientItem} htmlFor={`signer-${idx}`}>
                          <input
                            id={`signer-${idx}`}
                            type="checkbox"
                            checked={isSingleSignerSelected(s)}
                            disabled={!s.canSelect}
                            onChange={() => toggleSignerSelection(s)}
                            aria-label={`${s.name} (${s.email})`}
                          />
                          <div className={styles.recipientInfo}>
                            <div className={styles.recipientName}>{s.name}</div>
                            {<div className = {styles.recipientDeliveryMode}>{deliveryModeToShow(s)}</div>}
                          </div>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
              
              {reminderError && (
                <div className={styles.revokeError}>
                  <div className={styles.revokeErrorIcon}>
                    <span className={styles.sfIconWarning}></span>
                  </div>
                  <p>{reminderError}</p>
                </div>
              )}
              
              <p className={styles.revokeQuestion}>Send Message with your reminder</p>
              <textarea
                className={styles.reminderMessageInput}
                placeholder="Type here"
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                rows={3}
                required
              />
                     
              <div className={styles.reminderActions}>
                <button 
                  className={styles.cancelButton} 
                  onClick={() => {
                    setShowReminderModal(false);
                    setReminderMessage('');
                    setReminderError('');
                  }} 
                  disabled={isSendingReminder}
                >
                  Cancel
                </button>
                <button 
                  className={styles.revokeButton} 
                  onClick={handleSendReminder} 
                  disabled={isSendingReminder || (selectedSignerEmails.length === 0 && selectedSignerPhoneNumbers.length === 0) || isLoadingSigners}
                >
                  {isSendingReminder ? 'Sending...' : 'Remind Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDocuments;